import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Search, Filter, MoreVertical, Copy, Mail,
  Users, Clock, CheckCircle, AlertCircle, BarChart3, Trash2,
  ExternalLink, Settings, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AssessmentsListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('company_token');
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchAssessments(token);
  }, [navigate]);

  const fetchAssessments = async (token) => {
    try {
      const res = await axios.get(`${API}/api/company/assessments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessments(res.data.assessments || []);
    } catch (err) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const copyAccessCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Access code copied!');
  };

  const toggleActive = async (assessmentId, currentStatus) => {
    try {
      const token = localStorage.getItem('company_token');
      await axios.put(`${API}/api/company/assessments/${assessmentId}`, 
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAssessments(prev => prev.map(a => 
        a.id === assessmentId ? { ...a, is_active: !currentStatus } : a
      ));
      toast.success(currentStatus ? 'Assessment deactivated' : 'Assessment activated');
    } catch (err) {
      toast.error('Failed to update assessment');
    }
  };

  const deleteAssessment = async (assessmentId) => {
    if (!window.confirm('Are you sure you want to delete this assessment? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('company_token');
      await axios.delete(`${API}/api/company/assessments/${assessmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssessments(prev => prev.filter(a => a.id !== assessmentId));
      toast.success('Assessment deleted');
    } catch (err) {
      toast.error('Failed to delete assessment');
    }
  };

  const filteredAssessments = assessments.filter(a => {
    const matchesSearch = a.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'active' && a.is_active) ||
      (filterStatus === 'inactive' && !a.is_active);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/company/dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Assessments</h1>
            </div>
            
            <Link
              to="/company/assessments/create"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
              data-testid="create-new-assessment-btn"
            >
              <Plus size={18} />
              New Assessment
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search assessments..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="assessment-search-input"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
            data-testid="assessment-filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Assessments Grid */}
        {filteredAssessments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAssessments.map(assessment => (
              <div 
                key={assessment.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`assessment-card-${assessment.id}`}
              >
                {/* Card Header */}
                <div className="p-5 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg line-clamp-1">{assessment.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {assessment.total_mcqs} MCQs • {assessment.total_coding_problems} Coding
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      assessment.is_active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {assessment.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div className="p-5 grid grid-cols-3 gap-4 bg-gray-50">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{assessment.candidates_invited || 0}</p>
                    <p className="text-xs text-gray-500">Invited</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{assessment.candidates_started || 0}</p>
                    <p className="text-xs text-gray-500">Started</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{assessment.candidates_completed || 0}</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </div>
                </div>

                {/* Access Code */}
                <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Access Code:</span>
                    <code className="px-2 py-0.5 bg-gray-100 rounded text-sm font-mono text-gray-700">
                      {assessment.access_code}
                    </code>
                  </div>
                  <button
                    onClick={() => copyAccessCode(assessment.access_code)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    title="Copy access code"
                  >
                    <Copy size={14} />
                  </button>
                </div>

                {/* Actions */}
                <div className="p-4 border-t border-gray-100 flex items-center gap-2">
                  <Link
                    to={`/company/assessments/${assessment.id}`}
                    className="flex-1 py-2 text-center text-sm font-medium text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    View Details
                  </Link>
                  <Link
                    to={`/company/assessments/${assessment.id}/analytics`}
                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Analytics"
                  >
                    <BarChart3 size={18} />
                  </Link>
                  <button
                    onClick={() => toggleActive(assessment.id, assessment.is_active)}
                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title={assessment.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {assessment.is_active ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  <button
                    onClick={() => deleteAssessment(assessment.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="text-gray-400" size={28} />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No assessments found</h3>
            <p className="text-gray-500 mt-1">
              {searchQuery || filterStatus !== 'all' 
                ? 'Try adjusting your search or filter'
                : 'Create your first assessment to get started'}
            </p>
            {!searchQuery && filterStatus === 'all' && (
              <Link
                to="/company/assessments/create"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium"
              >
                <Plus size={18} />
                Create Assessment
              </Link>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AssessmentsListPage;
